import { z } from 'zod';

/** i18n key prefix for the disabled-checkbox tooltip messages. */
const INVITE_DISABLED_PREFIX = 'pages.members.inviteDisabled';

const emailSchema = z.string().email();

export const isEmailEmpty = (email: string | null): boolean => !email || email.trim() === '';

/** A non-empty, well-formed email. Mirrors the schema's `z.string().email()` rule. */
export const isEmailValid = (email: string | null): boolean =>
  !isEmailEmpty(email) && emailSchema.safeParse(email).success;

interface InvitabilityParams {
  groupId: string | null;
  email: string | null;
  /**
   * Whether the organization has a default group. When it does, a row with no explicit group is
   * still invitable — the invite is sent with the default group, which is what the group cell
   * shows in place of an empty value.
   */
  hasDefaultGroup: boolean;
}

/**
 * A member can be invited only when a group will be applied — either one picked on the row or the
 * organization's default — AND they have a valid non-empty email.
 * (status === Basic is guaranteed by the invite-mode query filter.)
 */
export const isInvitable = ({ groupId, email, hasDefaultGroup }: InvitabilityParams): boolean =>
  (groupId !== null || hasDefaultGroup) && isEmailValid(email);

/**
 * Resolves the i18n key for the disabled-checkbox tooltip from LIVE row values.
 * Returns `undefined` when the row is fully valid (no tooltip, checkbox enabled).
 */
export const getInviteDisabledKey = ({
  groupId,
  email,
  hasDefaultGroup,
}: InvitabilityParams): string | undefined => {
  const noGroup = groupId === null && !hasDefaultGroup;
  const badEmail = !isEmailValid(email);

  if (!noGroup && !badEmail) return undefined;
  if (noGroup && badEmail) return `${INVITE_DISABLED_PREFIX}.emailAndGroup`;
  if (badEmail) return `${INVITE_DISABLED_PREFIX}.email`;
  return `${INVITE_DISABLED_PREFIX}.group`;
};
